import {FutureCalc, IFutureYear, IIncomeDetail} from "../../../../components/calculator/Calculator";
import {Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from "@mui/material";
import React, {useEffect, useState} from "react";
import {numberToMoneyFormat} from "../../../../stores/StoreUtilities";
import globalStyles from "../../../../styles/globalStyles";
import {Gender} from "../../../../API";
import Income, {IncomeType} from "../../../../model/Income";


const SocialSecurityBenefitTable = ({
  futureCalc,
  year,
  onChange
}: {
  futureCalc: FutureCalc
  year?: number
  onChange?: (year: number) => any
}) => {
  const [currentYear, setCurrentYear] = useState<number>(0)
  const [incomes, setIncomes] = useState<Income[]>([])

  useEffect(() => {
    if (futureCalc && futureCalc.model && futureCalc.futureYears.length > 0) {
      const ssIncomes = futureCalc.model.incomes.filter((i: Income) => i.incomeType === IncomeType.SocialSecurityBenefits && i.amount > 0)
      setIncomes(ssIncomes)
    }
  }, [futureCalc])

  useEffect(() => {
    if (year) {
      setCurrentYear(year)
    }
  }, [year])

  const handleChange = (year: number) => {
    if (onChange) {
      setCurrentYear(year)
      onChange(year)
    }
  }

  const styles = {
    total: {
      fontWeight: 700
    }
  }

  if (incomes.length === 0) {
    return null
  }

  const totals = new Array<number>(incomes.length + 1)
  totals.fill(0)

  return (
    <Box display="flex" flexGrow={1} flexDirection="column" mt={2}>
      <Box display="flex" flexGrow={1} justifyContent="space-between" minHeight={40}>
        <Typography variant="h3" color="primary" gutterBottom>Social Security Benefit Details</Typography>
        <Box display="flex" alignItems="center" pt={0}>
          <Box width={14} height={14} sx={globalStyles.trRetired}></Box>
          <Typography variant="body2" style={globalStyles.legendLabel}>Planned Retirement</Typography>
          {futureCalc.person1 &&
            <React.Fragment>
              <Box width={14} height={14} sx={futureCalc.person1.gender === Gender.Male ? globalStyles.trHisLifeExpectancy : globalStyles.trHerLifeExpectancy}></Box>
              <Typography variant="body2" style={globalStyles.legendLabel}>{futureCalc.person1.nickname}'s Life Expectancy</Typography>
            </React.Fragment>
          }
          {futureCalc.person2 &&
            <React.Fragment>
              <Box width={14} height={14} sx={futureCalc.person2.gender === Gender.Male ? globalStyles.trHisLifeExpectancy : globalStyles.trHerLifeExpectancy}></Box>
              <Typography variant="body2" style={globalStyles.legendLabel}>{futureCalc.person2.nickname}'s Life Expectancy</Typography>
            </React.Fragment>
          }
          <Box width={14} height={14} sx={globalStyles.trAssetsDepleted}></Box>
          <Typography variant="body2" style={globalStyles.legendLabel}>Liquid Assets Final Year</Typography>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small" width="100%">
          <TableHead>
            <TableRow>
              {/*<TableCell width={30}></TableCell>*/}
              <TableCell>Year</TableCell>
              {incomes.map((income: Income) =>
                <TableCell align="right">{income.description}</TableCell>
              )}
              <TableCell align="right">Total Benefits</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {futureCalc.futureYears.map((fy: IFutureYear, index: number) => {
                const details = fy.incomeDetails

                let rowStyle
                if (fy.year === futureCalc.latestRetirementYear) {
                  rowStyle = globalStyles.trRetired
                } else if (futureCalc.person1 && fy.year === futureCalc.person1!.lifeExpectancyYear) {
                  rowStyle = (futureCalc.person1.gender === Gender.Male) ? globalStyles.trHisLifeExpectancy : globalStyles.trHerLifeExpectancy
                } else if (futureCalc.person2 && fy.year === futureCalc.person2!.lifeExpectancyYear) {
                  rowStyle = (futureCalc.person2.gender === Gender.Male) ? globalStyles.trHisLifeExpectancy : globalStyles.trHerLifeExpectancy
                } else if (fy.currentLiquidAssets > 0 && fy.spendableLiquidAssets <= 0) {
                  rowStyle = globalStyles.trAssetsDepleted
                }
                let yearTotal = 0

                return (
                <TableRow selected={currentYear === fy.year} key={`row${index}`}style={rowStyle}
                          onClick={() => {handleChange(fy.year)}}>
                  <TableCell>{fy.year}</TableCell>
                  {incomes.map((income: Income, index: number) => {
                    let amount = 0
                    if (income.owner && income.owner.lifeExpectancyYear >= fy.year) {
                      let detail = details.find((d: IIncomeDetail) => d.id === income.id)
                      if (detail) {
                        amount = detail.amount
                        totals[index] += amount
                        yearTotal += amount
                        totals[totals.length-1] += amount
                      } else if (income.owner.spouse && income.owner.spouse.lifeExpectancyYear < fy.year) {
                        // Look for survivor benefit, but only for one income
                        const spouseId = income.owner.spouse.id
                        const spousalBenefitIncome = incomes.find((i: Income, num: number) => num > index && i.incomeType === IncomeType.SocialSecurityBenefits && i.ownerId === income.ownerId)
                        if (!spousalBenefitIncome) {
                          detail = details.find((d: IIncomeDetail) => {
                            const index = incomes.findIndex((i: Income) => i.id === d.id && i.incomeType === IncomeType.SocialSecurityBenefits && i.ownerId === spouseId)
                            return index >= 0
                          })
                          if (detail) {
                            amount = detail.amount
                            totals[index] += amount
                            yearTotal += amount
                            totals[totals.length-1] += amount
                          }
                        }
                      }
                    }
                    return (<TableCell align="right">{numberToMoneyFormat(amount, 0)}</TableCell>)
                  })}
                  <TableCell align="right">{numberToMoneyFormat(yearTotal, 0)}</TableCell>
                </TableRow>
              )
            }
            )}
            <TableRow key={`totals`}>
              <TableCell sx={styles.total}>Lifetime Benefits</TableCell>
              {totals.map((total: number, index: number) =>
                <TableCell sx={styles.total} align="right">{numberToMoneyFormat(totals[index], 0)}</TableCell>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default SocialSecurityBenefitTable